import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { IAgreementInstallment, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";

interface Props {
	open: boolean;
	agreementId: string;
	installments: IAgreementInstallment[];
	onClose: () => void;
	onConfirm: () => void;
}

const isEligible = (installment: IAgreementInstallment) => installment.status !== "paid" && installment.status !== "canceled";

export const ModalBulkCreateBillet = ({ open, agreementId, installments, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const eligible = installments.filter(isEligible);
	const skippedCount = installments.length - eligible.length;

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const results = await Promise.all(eligible.map((installment) => SinisterAgreementService.createBillet(agreementId, installment.number)));
		setIsSubmitting(false);

		const failures = results.filter((result) => result instanceof Error) as Error[];
		const successCount = results.length - failures.length;

		if (successCount > 0) {
			toastHandler.present({
				type: failures.length > 0 ? "warning" : "success",
				messageOrContent:
					failures.length > 0
						? `${successCount} boleto(s) gerado(s) e enviado(s). ${failures.length} falharam.`
						: `${successCount} boleto(s) gerado(s) e enviado(s) por e-mail ao segurado.`,
				position: "top-right",
			});
		} else if (failures.length > 0) {
			toastHandler.present({ type: "error", messageOrContent: failures[0].message, position: "top-right" });
		}

		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Gerar/Reenviar Boletos em Massa
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="info" sx={{ py: 0.5 }}>
							{eligible.length} parcela(s) selecionada(s) — total{" "}
							{formatScaleValueBR(String(eligible.reduce((sum, installment) => sum + installment.value, 0)))}. Isso irá gerar o boleto no
							Sicoob e enviar por e-mail ao segurado para cada uma delas.
						</Alert>

						{skippedCount > 0 && (
							<Alert severity="info" sx={{ py: 0.5 }}>
								{skippedCount} parcela(s) selecionada(s) já paga(s) ou cancelada(s) serão ignoradas.
							</Alert>
						)}
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Box flex={1} />
					<Button variant="contained" disabled={isSubmitting || eligible.length === 0} onClick={handleConfirm}>
						Gerar/Reenviar ({eligible.length})
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
